import * as THREE from 'three';
import type {
  ActiveSelectionTool,
  SelectionDragSession,
  SelectionToolContext,
} from './activeSelectionTool';
import { computeApproximateLocalScaleFactors } from '@/utils/mathUtils';

type SelectHandleMode = 'corner' | 'moveY' | 'resizeY' | 'region';
type Corner = 'pos' | 'neg';

type CornerDragState = {
  mode: 'corner';
  plane: THREE.Plane;
  origin: THREE.Vector3;
  baseDeltaX: number;
  baseDeltaZ: number;
  initialStates: Array<{
    object: SelectionToolContext['selectedObjects'][number];
    position: THREE.Vector3;
    scale: THREE.Vector3;
    quaternion: THREE.Quaternion;
  }>;
};

type MoveYDragState = {
  mode: 'moveY';
  plane: THREE.Plane;
  startPoint: THREE.Vector3;
  initialStates: Array<{
    object: SelectionToolContext['selectedObjects'][number];
    position: THREE.Vector3;
  }>;
};

type ResizeYDragState = {
  mode: 'resizeY';
  plane: THREE.Plane;
  startPoint: THREE.Vector3;
  origin: THREE.Vector3;
  baseExtent: number;
  initialStates: Array<{
    object: SelectionToolContext['selectedObjects'][number];
    position: THREE.Vector3;
    scale: THREE.Vector3;
    quaternion: THREE.Quaternion;
    offsetFromOrigin: THREE.Vector3;
  }>;
};

type RegionMoveDragState = {
  mode: 'region';
  plane: THREE.Plane;
  startPoint: THREE.Vector3;
  initialStates: Array<{
    object: SelectionToolContext['selectedObjects'][number];
    position: THREE.Vector3;
  }>;
};

type SelectDragState =
  | CornerDragState
  | MoveYDragState
  | ResizeYDragState
  | RegionMoveDragState;

const MIN_SCALE_COMPONENT = 0.05;
const SCALE_SNAP_STEP = 0.1;
const WORLD_X_AXIS = new THREE.Vector3(1, 0, 0);
const WORLD_Y_AXIS = new THREE.Vector3(0, 1, 0);
const WORLD_Z_AXIS = new THREE.Vector3(0, 0, 1);

export class SelectSelectionTool implements ActiveSelectionTool {
  readonly id = 'select' as const;

  private readonly center = new THREE.Vector3();
  private readonly size = new THREE.Vector3();
  private readonly gizmoRoot = new THREE.Group();
  private readonly pickTargets: THREE.Object3D[] = [];

  private readonly cornerHandles: Array<{
    corner: Corner;
    visual: THREE.Mesh;
    pick: THREE.Mesh;
  }> = [];

  private readonly moveYGroup = new THREE.Group();
  private readonly moveYShaft: THREE.Mesh;
  private readonly moveYCone: THREE.Mesh;
  private readonly moveYPick: THREE.Mesh;

  private readonly resizeYVisual: THREE.Mesh;
  private readonly resizeYPick: THREE.Mesh;

  private readonly regionPick: THREE.Mesh;
  private visible = false;

  constructor() {
    this.gizmoRoot.visible = false;

    this.moveYShaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, 1, 10),
      new THREE.MeshBasicMaterial({
        color: 0x6cf08a,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      }),
    );
    this.moveYShaft.renderOrder = 1000;

    this.moveYCone = new THREE.Mesh(
      new THREE.ConeGeometry(0.09, 0.22, 12),
      new THREE.MeshBasicMaterial({
        color: 0x6cf08a,
        depthTest: false,
      }),
    );
    this.moveYCone.renderOrder = 1001;

    this.moveYPick = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.18, 1.3, 8),
      new THREE.MeshBasicMaterial({ visible: false, depthTest: false }),
    );
    this.moveYPick.userData.mode = 'moveY' as SelectHandleMode;
    this.moveYPick.userData.dragPriority = 1;

    this.moveYGroup.add(this.moveYShaft);
    this.moveYGroup.add(this.moveYCone);
    this.moveYGroup.add(this.moveYPick);
    this.gizmoRoot.add(this.moveYGroup);
    this.pickTargets.push(this.moveYPick);

    this.resizeYVisual = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.18, 0.18),
      new THREE.MeshBasicMaterial({
        color: 0xf4d35e,
        depthTest: false,
        transparent: true,
        opacity: 0.96,
      }),
    );
    this.resizeYVisual.renderOrder = 1002;

    this.resizeYPick = new THREE.Mesh(
      new THREE.SphereGeometry(0.2, 10, 10),
      new THREE.MeshBasicMaterial({ visible: false, depthTest: false }),
    );
    this.resizeYPick.userData.mode = 'resizeY' as SelectHandleMode;
    this.resizeYPick.userData.dragPriority = 2;

    this.gizmoRoot.add(this.resizeYVisual);
    this.gizmoRoot.add(this.resizeYPick);
    this.pickTargets.push(this.resizeYPick);

    this.regionPick = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ visible: false, depthTest: false }),
    );
    this.regionPick.userData.mode = 'region' as SelectHandleMode;
    this.regionPick.userData.dragPriority = 0;
    this.gizmoRoot.add(this.regionPick);
    this.pickTargets.push(this.regionPick);

    this.createCornerHandles();
  }

  render(parent: THREE.Object3D): void {
    parent.add(this.gizmoRoot);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.gizmoRoot.visible = visible;
  }

  getPickTargets(): THREE.Object3D[] {
    if (!this.visible) {
      return [];
    }

    return this.pickTargets;
  }

  updateTransform(center: THREE.Vector3, size: THREE.Vector3): void {
    this.center.copy(center);
    this.size.copy(size);

    this.gizmoRoot.position.copy(this.center);

    const half = this.size.clone().multiplyScalar(0.5);
    this.regionPick.scale.set(
      Math.max(this.size.x, 0.2),
      Math.max(this.size.y, 0.2),
      Math.max(this.size.z, 0.2),
    );

    for (const handle of this.cornerHandles) {
      const offset =
        handle.corner === 'pos'
          ? new THREE.Vector3(half.x, -half.y, half.z)
          : new THREE.Vector3(-half.x, -half.y, -half.z);
      handle.visual.position.copy(offset);
      handle.pick.position.copy(offset);
    }

    const arrowBaseY = -half.y + 0.1;
    const arrowLength = Math.max(this.size.y + 0.8, 1.2);

    this.moveYShaft.scale.y = arrowLength;
    this.moveYShaft.position.set(0, arrowBaseY + arrowLength * 0.5, 0);

    this.moveYCone.position.set(0, arrowBaseY + arrowLength + 0.14, 0);

    this.moveYPick.scale.y = arrowLength;
    this.moveYPick.position.set(0, arrowBaseY + arrowLength * 0.5, 0);

    const resizeY = arrowBaseY + arrowLength + 0.4;
    this.resizeYVisual.position.set(0, resizeY, 0);
    this.resizeYPick.position.set(0, resizeY, 0);
  }

  startDrag(context: SelectionToolContext): SelectionDragSession | null {
    const mode = context.intersection.object.userData.mode as
      | SelectHandleMode
      | undefined;

    if (!mode) {
      return null;
    }

    if (mode === 'region') {
      return this.startRegionDrag(context);
    }

    if (mode === 'corner') {
      const corner = context.intersection.object.userData.corner as
        | Corner
        | undefined;
      if (!corner) {
        return null;
      }
      return this.startCornerDrag(context, corner);
    }

    if (mode === 'moveY') {
      return this.startMoveYDrag(context);
    }

    return this.startResizeYDrag(context);
  }

  dispose(): void {
    for (const child of this.gizmoRoot.children) {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(material => material.dispose());
        } else {
          child.material.dispose();
        }
      }
    }
  }

  private startCornerDrag(
    context: SelectionToolContext,
    corner: Corner,
  ): SelectionDragSession | null {
    const halfSize = this.size.clone().multiplyScalar(0.5);
    const minCorner = this.center.clone().sub(halfSize);
    const maxCorner = this.center.clone().add(halfSize);

    const draggedCorner =
      corner === 'pos'
        ? new THREE.Vector3(maxCorner.x, minCorner.y, maxCorner.z)
        : new THREE.Vector3(minCorner.x, minCorner.y, minCorner.z);
    const origin =
      corner === 'pos'
        ? new THREE.Vector3(minCorner.x, minCorner.y, minCorner.z)
        : new THREE.Vector3(maxCorner.x, minCorner.y, maxCorner.z);

    const baseDeltaX = draggedCorner.x - origin.x;
    const baseDeltaZ = draggedCorner.z - origin.z;
    if (Math.abs(baseDeltaX) < 1e-4 || Math.abs(baseDeltaZ) < 1e-4) {
      return null;
    }

    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3(0, 1, 0),
      draggedCorner,
    );

    const startPoint = context.ray.intersectPlane(plane, new THREE.Vector3());
    if (!startPoint) {
      return null;
    }

    const dragState: CornerDragState = {
      mode: 'corner',
      plane,
      origin,
      baseDeltaX,
      baseDeltaZ,
      initialStates: context.selectedObjects.map(object => ({
        object,
        position: object.position.clone(),
        scale: object.scale.clone(),
        quaternion: object.quaternion.clone(),
      })),
    };

    return this.createDragSession(dragState);
  }

  private startMoveYDrag(
    context: SelectionToolContext,
  ): SelectionDragSession | null {
    const cameraDirection = new THREE.Vector3();
    context.camera.getWorldDirection(cameraDirection);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      cameraDirection.normalize(),
      this.center,
    );

    const startPoint = context.ray.intersectPlane(plane, new THREE.Vector3());
    if (!startPoint) {
      return null;
    }

    const dragState: MoveYDragState = {
      mode: 'moveY',
      plane,
      startPoint: startPoint.clone(),
      initialStates: context.selectedObjects.map(object => ({
        object,
        position: object.position.clone(),
      })),
    };

    return this.createDragSession(dragState);
  }

  private startResizeYDrag(
    context: SelectionToolContext,
  ): SelectionDragSession | null {
    const cameraDirection = new THREE.Vector3();
    context.camera.getWorldDirection(cameraDirection);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      cameraDirection.normalize(),
      this.center,
    );

    const startPoint = context.ray.intersectPlane(plane, new THREE.Vector3());
    if (!startPoint) {
      return null;
    }

    const origin = this.center.clone();
    origin.y = this.center.y - this.size.y * 0.5;

    const dragState: ResizeYDragState = {
      mode: 'resizeY',
      plane,
      startPoint: startPoint.clone(),
      origin,
      baseExtent: Math.max(this.size.y, 0.5),
      initialStates: context.selectedObjects.map(object => ({
        object,
        position: object.position.clone(),
        scale: object.scale.clone(),
        quaternion: object.quaternion.clone(),
        offsetFromOrigin: object.position.clone().sub(origin),
      })),
    };

    return this.createDragSession(dragState);
  }

  private startRegionDrag(
    context: SelectionToolContext,
  ): SelectionDragSession | null {
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    plane.constant = -this.center.y;

    const startPoint = context.ray.intersectPlane(plane, new THREE.Vector3());
    if (!startPoint) {
      return null;
    }

    const dragState: RegionMoveDragState = {
      mode: 'region',
      plane,
      startPoint: startPoint.clone(),
      initialStates: context.selectedObjects.map(object => ({
        object,
        position: object.position.clone(),
      })),
    };

    return this.createDragSession(dragState);
  }

  private createDragSession(dragState: SelectDragState): SelectionDragSession {
    return {
      updateDrag: (ray, snapToHalfStep) => {
        const dragPoint = ray.intersectPlane(
          dragState.plane,
          new THREE.Vector3(),
        );
        if (!dragPoint) {
          return;
        }

        if (dragState.mode === 'corner') {
          let targetCornerX = dragPoint.x;
          let targetCornerZ = dragPoint.z;

          if (snapToHalfStep) {
            targetCornerX = Math.round(targetCornerX / 0.5) * 0.5;
            targetCornerZ = Math.round(targetCornerZ / 0.5) * 0.5;
          }

          let factorX =
            (targetCornerX - dragState.origin.x) / dragState.baseDeltaX;
          let factorZ =
            (targetCornerZ - dragState.origin.z) / dragState.baseDeltaZ;

          factorX = Math.max(MIN_SCALE_COMPONENT, factorX);
          factorZ = Math.max(MIN_SCALE_COMPONENT, factorZ);

          for (const state of dragState.initialStates) {
            const newX =
              dragState.origin.x +
              (state.position.x - dragState.origin.x) * factorX;
            const newZ =
              dragState.origin.z +
              (state.position.z - dragState.origin.z) * factorZ;

            const localScaleFactors = computeApproximateLocalScaleFactors(
              state.scale,
              state.quaternion,
              [
                {
                  axis: WORLD_X_AXIS,
                  factor: factorX,
                },
                {
                  axis: WORLD_Z_AXIS,
                  factor: factorZ,
                },
              ],
            );

            state.object.setPosition(newX, state.position.y, newZ);
            state.object.setScale(
              Math.max(
                MIN_SCALE_COMPONENT,
                state.scale.x * localScaleFactors.x,
              ),
              Math.max(
                MIN_SCALE_COMPONENT,
                state.scale.y * localScaleFactors.y,
              ),
              Math.max(
                MIN_SCALE_COMPONENT,
                state.scale.z * localScaleFactors.z,
              ),
            );
          }

          return;
        }

        if (dragState.mode === 'region') {
          const dragDelta = dragPoint.clone().sub(dragState.startPoint);
          let deltaX = dragDelta.x;
          let deltaZ = dragDelta.z;

          if (snapToHalfStep) {
            const selectedCount = dragState.initialStates.length;

            if (selectedCount > 1) {
              deltaX = Math.round(deltaX / 0.5) * 0.5;
              deltaZ = Math.round(deltaZ / 0.5) * 0.5;
            } else if (selectedCount === 1) {
              const basePosition = dragState.initialStates[0]!.position;
              const snappedX =
                Math.round((basePosition.x + deltaX) / 0.5) * 0.5;
              const snappedZ =
                Math.round((basePosition.z + deltaZ) / 0.5) * 0.5;
              deltaX = snappedX - basePosition.x;
              deltaZ = snappedZ - basePosition.z;
            }
          }

          for (const state of dragState.initialStates) {
            state.object.setPosition(
              state.position.x + deltaX,
              state.position.y,
              state.position.z + deltaZ,
            );
          }

          return;
        }

        const deltaY = dragPoint.y - dragState.startPoint.y;

        if (dragState.mode === 'moveY') {
          let delta = deltaY;
          if (snapToHalfStep) {
            const selectedCount = dragState.initialStates.length;
            if (selectedCount > 1) {
              delta = Math.round(delta / 0.5) * 0.5;
            } else if (selectedCount === 1) {
              const baseY = dragState.initialStates[0]!.position.y;
              const snapped = Math.round((baseY + delta) / 0.5) * 0.5;
              delta = snapped - baseY;
            }
          }

          for (const state of dragState.initialStates) {
            state.object.setPosition(
              state.position.x,
              state.position.y + delta,
              state.position.z,
            );
          }

          return;
        }

        let factor = 1 + deltaY / dragState.baseExtent;
        factor = Math.max(MIN_SCALE_COMPONENT, factor);

        if (snapToHalfStep) {
          factor = Math.round(factor / SCALE_SNAP_STEP) * SCALE_SNAP_STEP;
          factor = Math.max(MIN_SCALE_COMPONENT, factor);
        }

        for (const state of dragState.initialStates) {
          const parallelDistance = state.offsetFromOrigin.y;
          const scaledParallelY = parallelDistance * factor;
          const newPositionY = dragState.origin.y + scaledParallelY;

          const localScaleFactors = computeApproximateLocalScaleFactors(
            state.scale,
            state.quaternion,
            [
              {
                axis: WORLD_Y_AXIS,
                factor,
              },
            ],
          );

          state.object.setPosition(
            state.position.x,
            newPositionY,
            state.position.z,
          );
          state.object.setScale(
            Math.max(MIN_SCALE_COMPONENT, state.scale.x * localScaleFactors.x),
            Math.max(MIN_SCALE_COMPONENT, state.scale.y * localScaleFactors.y),
            Math.max(MIN_SCALE_COMPONENT, state.scale.z * localScaleFactors.z),
          );
        }
      },
    };
  }

  private createCornerHandles() {
    const corners: Corner[] = ['pos', 'neg'];

    for (const corner of corners) {
      const visual = new THREE.Mesh(
        new THREE.BoxGeometry(0.16, 0.16, 0.16),
        new THREE.MeshBasicMaterial({
          color: 0xf4d35e,
          depthTest: false,
          transparent: true,
          opacity: 0.95,
        }),
      );
      visual.renderOrder = 1002;

      const pick = new THREE.Mesh(
        new THREE.SphereGeometry(0.2, 10, 10),
        new THREE.MeshBasicMaterial({ visible: false, depthTest: false }),
      );
      pick.userData.mode = 'corner' as SelectHandleMode;
      pick.userData.corner = corner;
      pick.userData.dragPriority = 3;

      this.gizmoRoot.add(visual);
      this.gizmoRoot.add(pick);
      this.pickTargets.push(pick);
      this.cornerHandles.push({ corner, visual, pick });
    }
  }
}
